import { useCallback, type ReactElement } from 'react'

import type { Poem } from '@maria-cavalheiro-website/shared-types'
import { LiveRegion, PillButton } from '@maria-cavalheiro-website/shared-ui-web'

import { EbookPageView } from './ebook-page-view.tsx'
import { pageAnnouncement } from './ebook-pages.ts'
import { useFullscreenDialog } from './use-fullscreen-dialog.ts'

const DIALOG_LABEL = 'Leitura em tela cheia'
const CLOSE_LABEL = 'Fechar a leitura'
const PREVIOUS_LABEL = 'Poema anterior'
const NEXT_LABEL = 'Próximo poema'
const PLAY_LABEL = 'Reproduzir'
const PAUSE_LABEL = 'Pausar'
const COUNTER_LABEL = 'Página'
const STAGE_LABEL = 'Poema'

/** Prefixed so it cannot collide with `PoemArticle`'s `poema-<slug>`. */
const TITLE_ID = 'ebook-titulo'

export interface EbookReaderProps {
  readonly poem: Poem
  readonly index: number
  readonly total: number
  readonly isPlaying: boolean
  readonly onClose: () => void
  readonly onPrevious: () => void
  readonly onNext: () => void
  readonly onTogglePlay: () => void
}

/**
 * The fullscreen book. Container for the dialog's imperative half, and nothing
 * else — every piece of state it renders arrives as a prop from `useEbook`.
 *
 * THE CLOSE BUTTON CALLS `dialog.close()`, NOT `onClose`. It has to: closing the
 * React state without closing the element would leave a modal dialog in the top
 * layer with nothing rendered inside it. Going through the platform means the
 * button, the Escape key and the browser's own dismissal all take one path — the
 * `close` event — and `useFullscreenDialog` turns that single event back into
 * `onClose`. It is also what returns focus to the launcher, which the platform
 * does for free and a hand-rolled overlay has to remember.
 *
 * THE TRANSPORT BAR IS BELOW THE PAGE, in reach of a thumb, and its three controls
 * are in reading order: back, play, forward. Every one is a `PillButton`, so every
 * one clears § 2.5.8's 44×44 without this file having to think about it.
 */
export function EbookReader({
  poem,
  index,
  total,
  isPlaying,
  onClose,
  onPrevious,
  onNext,
  onTogglePlay,
}: EbookReaderProps): ReactElement {
  const { dialogRef, surfaceRef } = useFullscreenDialog(onClose)

  const requestClose = useCallback(() => {
    dialogRef.current?.close()
  }, [dialogRef])

  return (
    <dialog className="ebook" ref={dialogRef} aria-label={DIALOG_LABEL}>
      {/* THE FRAME IS WHAT GOES FULLSCREEN, not the dialog and not the document —
          see `use-fullscreen-dialog.ts` for the top-layer ordering that makes the
          document the one element it must never be. It carries its own ground in
          `_ebook.sass` for the same reason. */}
      <div className="ebook__frame" ref={surfaceRef}>
        <div className="ebook__bar ebook__bar--top">
          <PillButton
            variant="ghost"
            onClick={requestClose}
            icon="close"
            label={CLOSE_LABEL}
          >
            <span className="visually-hidden">{CLOSE_LABEL}</span>
          </PillButton>

          {/* `aria-hidden` on the digits, with the real sentence in the live
              region below: "3 / 23" read aloud out of context is a fraction. */}
          <p className="ebook__counter">
            <span className="visually-hidden">{`${COUNTER_LABEL} ${String(index + 1)} de ${String(total)}`}</span>
            <span aria-hidden="true">{`${String(index + 1)} / ${String(total)}`}</span>
          </p>
        </div>

        {/* A NAMED, FOCUSABLE REGION BECAUSE IT SCROLLS. A poem longer than the
            viewport is read by scrolling this box, and a scroll container whose
            content holds no focusable element is unreachable by keyboard alone —
            § 2.1.1, and axe's `scrollable-region-focusable` reports it. `tabindex`
            is what lets the arrow keys and Page Down work here; `role`/`aria-label`
            are what stop that tab stop announcing as nothing. */}
        <div className="ebook__stage" role="region" aria-label={STAGE_LABEL} tabIndex={0}>
          <EbookPageView poem={poem} titleId={TITLE_ID} />
        </div>

        <div className="ebook__bar ebook__bar--bottom">
          <PillButton
            variant="secondary"
            onClick={onPrevious}
            icon="chevron-left"
            label={PREVIOUS_LABEL}
          >
            <span className="visually-hidden">{PREVIOUS_LABEL}</span>
          </PillButton>

          {/* Labelled with the ACTION, like the theme toggle, and deliberately not
              `aria-pressed`: the label already changes, and a pressed state on top
              of a changing label announces the same fact twice. */}
          <PillButton
            variant="primary"
            onClick={onTogglePlay}
            icon={isPlaying ? 'pause' : 'play'}
          >
            {isPlaying ? PAUSE_LABEL : PLAY_LABEL}
          </PillButton>

          <PillButton
            variant="secondary"
            onClick={onNext}
            iconAfter="chevron-right"
            label={NEXT_LABEL}
          >
            <span className="visually-hidden">{NEXT_LABEL}</span>
          </PillButton>
        </div>

        {/* Off-canvas rather than absent: the page number and the title are both
            already on screen, so this channel exists only for the readers who
            cannot see them turn. `visually-hidden` rather than a modifier of its
            own — it is the same technique, and a second copy of it would be a
            second thing to get wrong. */}
        <LiveRegion
          message={pageAnnouncement(index, total, poem.title)}
          className="visually-hidden"
        />
      </div>
    </dialog>
  )
}
